import React, { useState, useEffect } from 'react';
import transcriptionService from '../services/transcriptionService';
import './TranscriptionSettings.css';

/**
 * Transcription Settings Component
 * Displays transcription controls and transcript management UI
 * Part of the main Settings menu
 * 
 * Props:
 * - isActive: boolean - Whether transcription is running
 * - userName: string - Current user name
 * - roomId: string - Current room ID
 * - onStatusChange: function - Callback when status changes
 */
const TranscriptionSettings = ({ isActive, userName, roomId, onStatusChange }) => {
  const [transcriptionActive, setTranscriptionActive] = useState(isActive);
  const [transcriptText, setTranscriptText] = useState('');
  const [captionCount, setCaptionCount] = useState(0);
  const [duration, setDuration] = useState('00:00:00');
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [language, setLanguage] = useState('en-US');
  const [expandedSection, setExpandedSection] = useState(null);
  const [pastTranscripts, setPastTranscripts] = useState([]);

  useEffect(() => {
    // Update transcript display
    const updateTranscript = () => {
      setTranscriptText(transcriptionService.getTranscript());
      setCaptionCount(transcriptionService.getCaptions().length);
      setDuration(transcriptionService.getTimestamp());
    };

    if (!roomId) return; // Don't run if roomId not set

    // Listen to transcription events
    transcriptionService.on('caption-update', updateTranscript);
    transcriptionService.on('listening-started', () => {
      setTranscriptionActive(true);
      onStatusChange?.(true);
    });
    transcriptionService.on('listening-stopped', () => {
      setTranscriptionActive(false);
      onStatusChange?.(false);
    });
    transcriptionService.on('saved', () => {
      setSaveStatus({ type: 'success', message: '✅ Transcript saved!' });
      setTimeout(() => setSaveStatus(null), 3000);
      // TODO: Reload past transcripts when room management is implemented
    });
    transcriptionService.on('save-error', () => {
      setSaveStatus({ type: 'error', message: '❌ Failed to save transcript' });
      setTimeout(() => setSaveStatus(null), 3000);
    });

    // Update every 500ms if active
    const interval = setInterval(updateTranscript, 500);

    return () => {
      clearInterval(interval);
      transcriptionService.off('caption-update', updateTranscript);
    };
  }, [onStatusChange]);

  /**
   * Load past transcripts from VPS
   */
  const loadPastTranscripts = async () => {
    const transcripts = await transcriptionService.getRoomTranscripts();
    setPastTranscripts(transcripts);
  };

  /**
   * Handle transcription toggle
   */
  const handleToggleTranscription = () => {
    if (transcriptionActive) {
      transcriptionService.stopListening();
    } else {
      transcriptionService.startListening();
    }
  };

  /**
   * Handle language change
   */
  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setLanguage(newLanguage);
    transcriptionService.setLanguage(newLanguage);
    console.log(`🎤 Language changed to: ${newLanguage}`);
  };

  /**
   * Handle save to VPS
   */
  const handleSaveTranscript = async () => {
    setIsSaving(true);
    try {
      await transcriptionService.saveToDatabase({
        meetingDate: new Date().toISOString(),
        participants: [userName]
      });
    } catch (error) {
      console.error('❌ Error saving transcript:', error);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle downloads
   */
  const handleDownloadTxt = () => {
    transcriptionService.downloadAsTxt();
    console.log('📥 Downloaded as TXT');
  };

  const handleDownloadVtt = () => {
    transcriptionService.downloadAsVtt();
    console.log('📥 Downloaded as VTT');
  };

  const handleDownloadJson = () => {
    transcriptionService.downloadAsJson();
    console.log('📥 Downloaded as JSON');
  };

  /**
   * Handle clear transcript
   */
  const handleClearTranscript = () => {
    if (window.confirm('Clear transcript? This cannot be undone.')) {
      transcriptionService.clear();
      setTranscriptText('');
      setCaptionCount(0);
      console.log('🎤 Transcript cleared');
    }
  };

  /**
   * Handle viewing past transcript
   */
  const handleViewTranscript = async (fileName) => {
    const transcript = await transcriptionService.fetchSavedTranscript(fileName);
    if (transcript) {
      alert(`Transcript: ${fileName}\n\n${transcript.transcript}`);
    }
  };

  /**
   * Handle deleting past transcript
   */
  const handleDeletePastTranscript = async (fileName) => {
    if (window.confirm(`Delete transcript: ${fileName}?`)) {
      const success = await transcriptionService.deleteTranscript(fileName);
      if (success) {
        loadPastTranscripts();
      }
    }
  };

  return (
    <div className="transcription-settings">
      {/* Header */}
      <div className="settings-section-header">
        <div className="header-title">
          <span className="icon">🎤</span>
          <span>Transcription</span>
        </div>
        <div className={`status-badge ${transcriptionActive ? 'active' : 'inactive'}`}>
          {transcriptionActive ? 'Recording' : 'Ready'}
        </div>
      </div>

      {/* Main Toggle */}
      <div className="transcription-toggle-section">
        <button
          className={`transcription-toggle ${transcriptionActive ? 'active' : ''}`}
          onClick={handleToggleTranscription}
        >
          <span className="toggle-icon">{transcriptionActive ? '⏹️' : '🎙️'}</span>
          <span className="toggle-text">
            {transcriptionActive ? 'Stop Transcription' : 'Start Transcription'}
          </span>
        </button>
      </div>

      {/* Save Status Message */}
      {saveStatus && (
        <div className={`save-status ${saveStatus.type}`}>
          {saveStatus.message}
        </div>
      )}

      {/* Language Selector */}
      <div className="setting-group">
        <label className="setting-label">Language</label>
        <select
          className="language-select"
          value={language}
          onChange={handleLanguageChange}
          disabled={transcriptionActive}
        >
          <option value="en-US">English (US)</option>
          <option value="en-GB">English (UK)</option>
          <option value="es-ES">Spanish</option>
          <option value="fr-FR">French</option>
          <option value="de-DE">German</option>
          <option value="it-IT">Italian</option>
          <option value="ja-JP">Japanese</option>
          <option value="zh-CN">Chinese (Simplified)</option>
          <option value="pt-BR">Portuguese (Brazil)</option>
          <option value="ru-RU">Russian</option>
        </select>
      </div>

      {/* Stats */}
      {transcriptionActive && (
        <div className="transcript-stats">
          <div className="stat-item">
            <span className="stat-label">Duration</span>
            <span className="stat-value">{duration}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Captions</span>
            <span className="stat-value">{captionCount}</span>
          </div>
        </div>
      )}

      {/* Transcript Preview */}
      <div className="expandable-section">
        <button
          className="section-toggle"
          onClick={() => setExpandedSection(expandedSection === 'preview' ? null : 'preview')}
        >
          <span className="toggle-arrow">{expandedSection === 'preview' ? '▼' : '▶'}</span>
          <span>Transcript Preview</span>
          {transcriptText && <span className="section-count">({captionCount})</span>}
        </button>

        {expandedSection === 'preview' && (
          <div className="section-content">
            <div className="transcript-preview">
              {transcriptText ? (
                <pre>{transcriptText}</pre>
              ) : (
                <div className="empty-state">No transcript yet. Start recording to begin.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Save & Export Options */}
      {transcriptText && (
        <div className="expandable-section">
          <button
            className="section-toggle"
            onClick={() => setExpandedSection(expandedSection === 'save' ? null : 'save')}
          >
            <span className="toggle-arrow">{expandedSection === 'save' ? '▼' : '▶'}</span>
            <span>Save & Export</span>
          </button>

          {expandedSection === 'save' && (
            <div className="section-content">
              <div className="save-options">
                {/* Save to VPS */}
                <button
                  className="action-button primary"
                  onClick={handleSaveTranscript}
                  disabled={isSaving}
                >
                  {isSaving ? '💾 Saving...' : '💾 Save to VPS'}
                </button>

                {/* Download Options */}
                <div className="download-group">
                  <button
                    className="action-button secondary"
                    onClick={handleDownloadTxt}
                  >
                    📄 Download as TXT
                  </button>
                  <button
                    className="action-button secondary"
                    onClick={handleDownloadVtt}
                  >
                    🎬 Download as VTT (Subtitles)
                  </button>
                  <button
                    className="action-button secondary"
                    onClick={handleDownloadJson}
                  >
                    🔗 Download as JSON
                  </button>
                </div>

                {/* Clear */}
                <button
                  className="action-button danger"
                  onClick={handleClearTranscript}
                >
                  🗑️ Clear Transcript
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info */}
      <div className="transcription-info">
        <p>💡 Captions appear above the video grid. Transcripts are stored on VPS for 30 days.</p>
      </div>
    </div>
  );
};

export default TranscriptionSettings;
